import React, { useState, useEffect, useCallback } from 'react';
import '../styles/SearchPanel.css';

const SearchPanel = ({ onSearch, onFilter }) => {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [blockchain, setBlockchain] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length > 0) {
        // Generate suggestions based on query
        setSuggestions([
          `${query}`,
          `address:${query}`,
          `title:${query}`,
          `status:${query}`
        ]);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSearch = useCallback(() => {
    const filters = {
      query,
      status: status || null,
      blockchain: blockchain || null,
      date_from: dateFrom || null,
      date_to: dateTo || null,
      sort_by: sortBy,
      order
    };
    onSearch?.(filters);
    onFilter?.(filters);
  }, [query, status, blockchain, dateFrom, dateTo, sortBy, order, onSearch, onFilter]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    setShowSuggestions(false);
  };

  const handleClearFilters = () => {
    setQuery('');
    setStatus('');
    setBlockchain('');
    setDateFrom('');
    setDateTo('');
    setSortBy('created_at');
    setOrder('desc');
    onSearch?.({});
  };

  return (
    <div className="search-panel">
      <div className="search-bar-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search investigations by title, address, or description..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        
        {showSuggestions && suggestions.length > 0 && (
          <div className="suggestions-dropdown">
            {suggestions.map((suggestion, idx) => (
              <div
                key={idx}
                className="suggestion-item"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <span className="suggestion-icon">🔍</span>
                {suggestion}
              </div>
            ))}
          </div>
        )}

        <button 
          className="search-button"
          onClick={handleSearch}
          title="Search"
        >
          🔍
        </button>

        <button
          className="filter-toggle"
          onClick={() => setShowFilters(!showFilters)}
          title="Toggle filters"
        >
          ⚙️
        </button>
      </div>

      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Blockchain</label>
            <select
              value={blockchain}
              onChange={(e) => setBlockchain(e.target.value)}
            >
              <option value="">All</option>
              <option value="tron">TRON</option>
              <option value="ethereum">Ethereum</option>
              <option value="polygon">Polygon</option>
              <option value="arbitrum">Arbitrum</option>
              <option value="bsc">BNB Chain (BSC)</option>
              <option value="bitcoin">Bitcoin</option>
              <option value="solana">Solana</option>
            </select>
          </div>

          <div className="filter-group">
            <label>From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="created_at">Created Date</option>
              <option value="updated_at">Updated Date</option>
              <option value="title">Title</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Order</label>
            <select
              value={order}
              onChange={(e) => setOrder(e.target.value)}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>

          <button
            className="clear-filters-btn"
            onClick={handleClearFilters}
          >
            Clear Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchPanel;
